import React from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { Palette, ExpandMore } from '@mui/icons-material';
import PropTypes from 'prop-types';
import ColorPickerControl from './ColorPickerControl.jsx';
import { MapStatsControls } from './MapStats.jsx';
import { PALETTE } from '../theme';

/**
 * Map-options accordion that sits ABOVE the map on every Travel Map section.
 *
 * Only the styling controls (visited color, in-map stat badge, per-map extras)
 * live here. Reset / Download / Share moved to <MapActions /> which renders
 * BELOW the map — actions follow the artifact they act on.
 */
const MapToolbar = ({
    visitedColor,
    onColorChange,
    onPresetClick,
    statsConfig,
    onStatsChange,
    accent,
    extraOptions = null
}) => (
    <Accordion
        disableGutters
        elevation={0}
        sx={{
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            '&:before': { display: 'none' },
            mb: { xs: 1, sm: 1.5 },
            '& .MuiAccordionSummary-root': { minHeight: 'auto', py: 0.5, px: 0 },
            '& .MuiAccordionDetails-root': { px: 0, pt: 1, pb: 0 }
        }}
    >
        <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
        >
            <Palette sx={{ color: PALETTE.primary }} />
            <Typography sx={{ fontWeight: 600, color: PALETTE.text }}>
                Map options
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: PALETTE.textMuted, display: { xs: 'none', sm: 'block' } }}
            >
                (visited color &amp; progress badge)
            </Typography>
        </AccordionSummary>
        <AccordionDetails>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Visited color:
                    </Typography>
                    <ColorPickerControl
                        color={visitedColor}
                        onChange={onColorChange}
                        onPresetClick={onPresetClick}
                    />
                </Box>
                <MapStatsControls config={statsConfig} onChange={onStatsChange} accent={accent} />
                {extraOptions}
            </Box>
        </AccordionDetails>
    </Accordion>
);

MapToolbar.propTypes = {
    visitedColor: PropTypes.object.isRequired,
    onColorChange: PropTypes.func.isRequired,
    onPresetClick: PropTypes.func.isRequired,
    statsConfig: PropTypes.object.isRequired,
    onStatsChange: PropTypes.func.isRequired,
    accent: PropTypes.string,
    extraOptions: PropTypes.node
};

export default MapToolbar;
